/**
 * UpSurv 2.0 — routing.
 *
 * HASH routing, not history routing. Two reasons, both about the field:
 *
 *  1. The app is launched from the home screen with no network (brief §6.6).
 *     Hash URLs never ask the origin for a path, so a deep link into a section
 *     cannot 404 against a cold service worker or a mis-set `navigateFallback`.
 *  2. A hash change is a same-document navigation, so a reload lands back on the
 *     screen the surveyor was on, which is what "force-quit mid-answer loses
 *     nothing" has to mean at the URL level too.
 *
 * Pure by construction: no React, no `window`, no side effects. That is what
 * lets tests/unit/ui-shell.test.ts run it under the node environment the rest of
 * the suite uses.
 *
 * The mockup's bottom screen-switcher is a MOCKUP ARTIFACT for demoing screens
 * and is deliberately not represented here. Real navigation is the section
 * indicator (`sections`) plus back/next inside a section.
 */

/* ───────────────────────────── the route table ───────────────────────────── */

export type Route =
  /** The survey list. */
  | { name: 'home' }
  /** Building info — filled out once per building (spec §3.1). */
  | { name: 'building'; surveyId: string }
  /** Room type, name, car list, groups, hoistways (spec §3.2). */
  | { name: 'roomSetup'; surveyId: string; roomId: string }
  /** The elevator hall indicator: this room's sections, in walk order. */
  | { name: 'sections'; surveyId: string; roomId: string }
  /** One section instance. `sectionId` is a canonical `sectionInstanceId()`. */
  | { name: 'section'; surveyId: string; sectionId: string }
  /** Ad hoc items, gaps, and Submit (spec §2.5). */
  | { name: 'submit'; surveyId: string }
  /** Surveyor registry / token minting (D-005). */
  | { name: 'admin' }
  /** Where the assignment SMS link lands: link this device, then get out of the way. */
  | { name: 'access' }
  /**
   * Where a scanned onboarding QR lands (D-013). Separate from `access`: that
   * screen checks a token the device already holds, this one SPENDS a one-time
   * code to get one, and the two fail in different ways for different reasons.
   */
  | { name: 'onboard' }
  /**
   * What is on this device, and the export/import bridge (D-020). Its own route
   * so the office can send somebody to it over the phone — "open This device" —
   * which is the whole of the diagnosis when a survey is in the other container.
   */
  | { name: 'device' }
  /**
   * The list of parked items the sync pill counts — WHICH photo, WHY, and what
   * to do (Chip, 2026-08-18: *"'X items need you' … gave no clarification about
   * what those items were, how to fix the issue, or what to do"*). Its own
   * route so the pill, which lives in the shell on every screen, has somewhere
   * to point.
   */
  | { name: 'attention' }
  | { name: 'notFound'; path: string };

export type RouteName = Route['name'];

/** The route a bad or empty hash falls back to. */
export const HOME: Route = { name: 'home' };

/* ────────────────────────────── formatting ──────────────────────────────── */

/**
 * `sectionInstanceId()` produces "room:r1|pit:h2" — both `:` and `|` are legal
 * in a URL fragment but neither survives being read back as a path segment
 * unencoded, so every id goes through encodeURIComponent on the way out and
 * decodeURIComponent on the way in.
 */
function seg(value: string): string {
  return encodeURIComponent(value);
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'home':
      return '#/';
    case 'building':
      return `#/s/${seg(route.surveyId)}/building`;
    case 'roomSetup':
      return `#/s/${seg(route.surveyId)}/room/${seg(route.roomId)}/setup`;
    case 'sections':
      return `#/s/${seg(route.surveyId)}/room/${seg(route.roomId)}/sections`;
    case 'section':
      return `#/s/${seg(route.surveyId)}/section/${seg(route.sectionId)}`;
    case 'submit':
      return `#/s/${seg(route.surveyId)}/submit`;
    case 'admin':
      return '#/admin';
    case 'access':
      return '#/access';
    case 'onboard':
      return '#/onboard';
    case 'device':
      return '#/device';
    case 'attention':
      return '#/needs-you';
    case 'notFound':
      return `#${route.path}`;
  }
}

/* ─────────────────────────────── parsing ────────────────────────────────── */

/** '#/s/abc/building?x=1' → ['s','abc','building']. Tolerates '#', '#!' and ''. */
export function pathSegments(hash: string): string[] {
  let path = hash;
  if (path.startsWith('#')) path = path.slice(1);
  if (path.startsWith('!')) path = path.slice(1);
  const queryAt = path.search(/[?]/);
  if (queryAt >= 0) path = path.slice(0, queryAt);
  return path
    .split('/')
    .filter((part) => part.length > 0)
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        // A hand-mangled '%' in a pasted link must not throw the whole app onto
        // an error screen; the segment simply will not match a route.
        return part;
      }
    });
}

export function parseRoute(hash: string): Route {
  const parts = pathSegments(hash);
  if (parts.length === 0) return HOME;

  if (parts[0] === 'admin' && parts.length === 1) return { name: 'admin' };
  if (parts[0] === 'access' && parts.length === 1) return { name: 'access' };
  if (parts[0] === 'onboard' && parts.length === 1) return { name: 'onboard' };
  if (parts[0] === 'device' && parts.length === 1) return { name: 'device' };
  if (parts[0] === 'needs-you' && parts.length === 1) return { name: 'attention' };

  if (parts[0] === 's') {
    const surveyId = parts[1];
    if (surveyId) {
      const rest = parts.slice(2);
      if (rest.length === 1 && rest[0] === 'building') return { name: 'building', surveyId };
      if (rest.length === 1 && rest[0] === 'submit') return { name: 'submit', surveyId };
      if (rest.length === 2 && rest[0] === 'section' && rest[1]) {
        return { name: 'section', surveyId, sectionId: rest[1] };
      }
      if (rest.length === 3 && rest[0] === 'room' && rest[1]) {
        const roomId = rest[1];
        if (rest[2] === 'setup') return { name: 'roomSetup', surveyId, roomId };
        if (rest[2] === 'sections') return { name: 'sections', surveyId, roomId };
      }
    }
  }

  return { name: 'notFound', path: `/${parts.join('/')}` };
}

/* ──────────────────────────── convenience ───────────────────────────────── */

/** The survey a route belongs to, or null for home / admin / not-found. */
export function surveyIdOf(route: Route): string | null {
  switch (route.name) {
    case 'building':
    case 'roomSetup':
    case 'sections':
    case 'section':
    case 'submit':
      return route.surveyId;
    default:
      return null;
  }
}

/**
 * Where the back button goes. Never `history.back()`: a surveyor who jumped
 * straight into a section from the indicator (spec §2.2 — jumping is expected)
 * would be sent to whatever screen they happened to come from, which is not the
 * structural parent. This walks the survey's structure instead.
 */
export function parentOf(route: Route): Route {
  switch (route.name) {
    case 'home':
    case 'admin':
    case 'access':
    case 'onboard':
    case 'device':
    case 'attention':
    case 'notFound':
      return HOME;
    case 'building':
    case 'submit':
      return HOME;
    case 'roomSetup':
      return { name: 'building', surveyId: route.surveyId };
    case 'sections':
      return { name: 'roomSetup', surveyId: route.surveyId, roomId: route.roomId };
    case 'section':
      // A section knows its room only through its scope, which the shell
      // resolves; without it the honest parent is the building screen.
      return { name: 'building', surveyId: route.surveyId };
  }
}
